/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import {
  useLayoutEffect,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from "react";
import { useHover } from "react-aria";
import { useResizeObserver } from "@react-aria/utils";

import { useLocale } from "@superweb/intl";
import { Button, useIsMobile } from "@superweb/ui";

import { useMessage } from "#intl";

import { useUiOptions } from "./ui-options-context";
import * as fleetIcons from "./icons";

export const RowWithScroll = ({
  children,
  disabledShadow = false,
  disabledAlign = false,
  scrollRef,
  alwaysShowArrows,
  marginInline,
  scrollSnapMandatory,
}: {
  children: ReactNode;
  disabledShadow?: boolean;
  disabledAlign?: boolean;
  scrollRef?: MutableRefObject<HTMLDivElement | null>;
  alwaysShowArrows?: boolean;
  marginInline?: string;
  scrollSnapMandatory?: boolean;
}) => {
  const isMobile = useIsMobile();
  const message = useMessage();
  const { experimental } = useUiOptions();

  const innerRef = useRef<HTMLDivElement>(null);
  const ref = scrollRef ?? innerRef;
  const resizeRef = useRef<HTMLDivElement>(null);
  const {
    textInfo: { direction },
  } = useLocale();

  const { hoverProps, isHovered } = useHover({});

  const shouldShowArrows = alwaysShowArrows || isMobile || isHovered;

  const [canScrollStart, setCanScrollStart] = useState(false);
  const [canScrollEnd, setCanScrollEnd] = useState(true);

  const scrollEnd = () => {
    if (!ref.current) return;
    const scrollContainer = ref.current;
    const scrollLeft = Math.abs(scrollContainer.scrollLeft);
    const restOfScrollEnd =
      scrollContainer.scrollWidth - scrollLeft - scrollContainer.clientWidth;
    const scrollByLeft = Math.min(scrollContainer.clientWidth, restOfScrollEnd);
    ref.current.scrollBy({
      left: direction === "ltr" ? scrollByLeft : -scrollByLeft,
      behavior: "smooth",
    });
  };

  const scrollStart = () => {
    if (!ref.current) return;
    const scrollContainer = ref.current;
    const scrollLeft = Math.abs(scrollContainer.scrollLeft);
    const scrollByLeft = Math.min(scrollLeft, scrollContainer.clientWidth);
    ref.current.scrollBy({
      left: direction === "ltr" ? -scrollByLeft : scrollByLeft,
      behavior: "smooth",
    });
  };

  useLayoutEffect(() => {
    if (!ref.current) return;
    const scrollContainer = ref.current;
    const checkScrollPosition = () => {
      const scrollLeft = Math.abs(scrollContainer.scrollLeft);
      setCanScrollStart(scrollLeft > 2);
      setCanScrollEnd(
        scrollContainer.scrollWidth - scrollLeft - scrollContainer.clientWidth >
          2,
      );
    };
    checkScrollPosition();
    scrollContainer.addEventListener("scroll", checkScrollPosition);
    return () =>
      scrollContainer.removeEventListener("scroll", checkScrollPosition);
  }, [ref]);

  const checkScrollPosition = () => {
    if (!ref.current) return;
    const scrollContainer = ref.current;
    const scrollLeft = Math.abs(scrollContainer.scrollLeft);
    setCanScrollStart(scrollLeft > 2);
    setCanScrollEnd(
      scrollContainer.scrollWidth - scrollLeft - scrollContainer.clientWidth >
        2,
    );
  };

  useResizeObserver({
    ref,
    onResize: checkScrollPosition,
  });

  useResizeObserver({
    ref: resizeRef,
    onResize: checkScrollPosition,
  });

  return (
    <div
      {...hoverProps}
      css={{
        position: "relative",
        display: "grid",
        alignContent: disabledAlign ? undefined : "center",
      }}
    >
      <div
        ref={ref}
        css={{
          display: experimental?.checkResizeInnerContainer ? undefined : "flex",
          overflowX: "auto",
          overflowY: experimental?.disableVerticalScroll ? "hidden" : undefined,
          scrollbarWidth: "none",
          scrollSnapType: scrollSnapMandatory ? "x mandatory" : undefined,
          ...(!disabledShadow && {
            maskImage: `linear-gradient(
              ${direction === "ltr" ? "to right" : "to left"},
              ${canScrollStart ? "transparent 0px, black 10px" : "black"},
              black calc(100% - 10px),
              ${canScrollEnd ? "transparent 100%" : "black"}
            )`,
          }),
          ...(marginInline
            ? {
                marginInlineStart: marginInline,
                marginInlineEnd: marginInline,
              }
            : {}),
        }}
        __experimental_webkitScrollbarCss={{ display: "none" }}
      >
        {experimental?.checkResizeInnerContainer ? (
          <div
            ref={resizeRef}
            css={{
              display: "flex",
              width: "fit-content",
            }}
          >
            {children}
          </div>
        ) : (
          children
        )}
      </div>
      {shouldShowArrows && canScrollStart && (
        <div
          css={{
            position: "absolute",
            insetBlockStart: "0",
            insetBlockEnd: "0",
            display: "grid",
            alignContent: "center",
          }}
        >
          <Button
            variant="floating"
            type="button"
            shape="circle"
            size="xs"
            ariaLabel={message({
              id: "d8bf0553-09b8-4b50-8862-6cef99f7b7d8",
              context: "Row with scroll. Arrow button",
              default: "Go back to previous",
            })}
            icon={fleetIcons.ArrowInlineStart}
            onPress={() => {
              scrollStart();
            }}
          />
        </div>
      )}
      {shouldShowArrows && canScrollEnd && (
        <div
          css={{
            position: "absolute",
            insetInlineEnd: "0px",
            insetBlockStart: "0",
            insetBlockEnd: "0",
            display: "grid",
            alignContent: "center",
          }}
        >
          <Button
            variant="floating"
            type="button"
            shape="circle"
            size="xs"
            ariaLabel={message({
              id: "a62e35e8-02df-4eaa-9e3f-7f09b36f5f43",
              context: "Row with scroll. Arrow button",
              default: "Go to next",
            })}
            icon={fleetIcons.ArrowInlineEnd}
            onPress={() => {
              scrollEnd();
            }}
          />
        </div>
      )}
    </div>
  );
};
